import { readTenantSlugFromUrl } from "../contexts/TenantContext";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

// Module-level slug cache. Resolved at first request (the SPA can only
// live on one tenant per page load, so re-parsing the URL per fetch is
// wasted work). Tests can reset by re-importing.
let _tenantSlug: string | null | undefined = undefined;
function tenantSlug(): string | null {
  if (_tenantSlug === undefined) {
    _tenantSlug = readTenantSlugFromUrl();
  }
  return _tenantSlug;
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {};

  if (!(options.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }

  // PR5 (Finding A4): include X-Tenant-Slug whenever we know the slug.
  //   - Platform subdomain (acme.app.goeswestservices.com): backend's
  //     RequestContextMiddleware verifies the header matches the Host-derived
  //     slug (deploy-misconfig check).
  //   - Firebase default site (gws-<slug>.web.app): backend's Host-based
  //     resolver returns None (no custom_domain match), so it falls back to
  //     X-Tenant-Slug to set the tenant context — without the header the
  //     SQLAlchemy hook RAISES on the first DB query. CORS allowlist in
  //     config.py admits the gws-*.web.app Origin so the preflight passes.
  //   - Custom-domain tenants (acme-hvac.com): tenantSlug() returns null;
  //     header is omitted; backend resolves via Tenant.custom_domain.
  const slug = tenantSlug();
  if (slug) {
    headers["X-Tenant-Slug"] = slug;
  }

  const url = `${BASE_URL}${path}`;
  let res: Response;
  try {
    res = await fetch(url, {
      ...options,
      headers:     { ...headers, ...options.headers },
      credentials: "include",   // browser sends gws_jwt HttpOnly cookie automatically
    });
  } catch (networkErr: any) {
    console.error(`[API] Network error for ${url}:`, networkErr);
    throw new Error(`Failed to reach server at ${BASE_URL} — is the backend running?`);
  }

  if (!res.ok) {
    if (res.status === 401) {
      // Cookie expired or invalidated — let the app know without touching localStorage
      window.dispatchEvent(new Event("gws:logout"));
    }
    let errorMessage = `Request failed: ${res.status} ${res.statusText}`;
    try {
      const err = await res.json();
      errorMessage = err.detail || err.message || errorMessage;
    } catch {}
    const error: any = new Error(errorMessage);
    error.status = res.status;
    throw error;
  }

  if (res.status === 204) return {} as T;

  return res.json();
}

/**
 * Reset the cached tenant slug. Tests use this to swap window.location
 * between cases. Production code never needs to call it — the slug can't
 * change without a page reload.
 */
export function _resetTenantSlugCacheForTests(): void {
  _tenantSlug = undefined;
}

export const api = {
  get:      <T>(path: string)                  => request<T>(path),
  post:     <T>(path: string, body: unknown)   => request<T>(path, { method: "POST",   body: JSON.stringify(body) }),
  postForm: <T>(path: string, form: FormData)  => request<T>(path, { method: "POST",   body: form }),
  put:      <T>(path: string, body: unknown)   => request<T>(path, { method: "PUT",    body: JSON.stringify(body) }),
  patch:    <T>(path: string, body: unknown)   => request<T>(path, { method: "PATCH",  body: JSON.stringify(body) }),
  delete:   <T>(path: string)                  => request<T>(path, { method: "DELETE" }),
};
